import type { ReactElement } from "react"
import OnColorContext from "@onestore/hel/dist/contexts/OnColorContext"
import AlignedHeader from "@gatsby-plugin-generic-page/components/AlignedHeader"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import type { ProductsTilesSectionFlatData } from "@gatsby-plugin-generic-page/fragments/productsTilesSection"
import { getBgColorClass } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import getGapClass from "@gatsby-plugin-generic-page/helpers/getGapClass"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import ProductTile from "./components/ProductTile"

const GRID_COLS_CLASS: Record<string, string> = {
  "1": "grid-cols-1",
  "2": "grid-cols-1 md:grid-cols-2",
  "3": "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  "4": "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  "5": "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
}

const MAX_AUTO_COLUMNS = 5

// Matches the gap ramp the section used before the grid rewrite, so existing
// content keeps its spacing when no explicit gap is configured in Squidex.
const DEFAULT_GAP_CLASS = "gap-3 md:gap-4 lg:gap-5"

// The side-by-side variants in the design give the header roughly a third of
// the row, except when only one or two tiles sit next to it — there the split
// is even.
const WIDE_HEADER_TILES_LIMIT = 2

function ProductsTilesSection({
  header,
  headerLayout,
  tiles,
  columnCount,
  gap,
  containerBackgroundColor,
  sectionSettings,
}: ProductsTilesSectionFlatData): ReactElement<ProductsTilesSectionFlatData> {
  const hasHeader = !isEmpty(header)
  const position = headerLayout?.position ?? "top"
  const isSideHeader = hasHeader && position !== "top"

  const autoColumns = String(
    Math.min(Math.max(tiles.length, 1), MAX_AUTO_COLUMNS)
  )
  const gridColsClass =
    GRID_COLS_CLASS[columnCount ?? autoColumns] ?? GRID_COLS_CLASS["3"]
  const gapClass = isEmpty(gap) ? DEFAULT_GAP_CLASS : getGapClass(gap)

  // `getBgColorClass` returns "" for an explicit "none", so an empty class
  // means the editor opted out of the container chrome entirely.
  const containerBgClass = getBgColorClass(containerBackgroundColor)
  const hasContainer = !isEmpty(containerBgClass)

  // Sizing for whichever element sits next to the header in the flex row. It is
  // scoped to `md:` because the row itself only exists from `md` up — applying
  // `flex-1` in the stacked column would give the tiles a zero flex-basis and
  // collapse their height.
  const rowChildClass = isSideHeader ? "md:min-w-0 md:flex-1" : "w-full"

  const grid = (
    <div className={cn("grid w-full", gridColsClass, gapClass)}>
      {tiles.map((tile) => (
        <ProductTile key={`product-tile-${tile.title}`} {...tile} />
      ))}
    </div>
  )

  const wrapInContainer = (
    children: ReactElement,
    className?: string
  ): ReactElement => (
    <div
      className={cn(
        "rounded-sm border border--base p-4 md:p-6 lg:p-8",
        containerBgClass,
        className
      )}
    >
      {children}
    </div>
  )

  const headerNode = hasHeader ? (
    <AlignedHeader
      header={header}
      alignMobile={headerLayout?.alignMobile}
      alignTablet={headerLayout?.alignTablet}
      alignDesktop={headerLayout?.alignDesktop}
      hasVerticalAlign={isSideHeader}
      className={cn(
        isSideHeader &&
          (tiles.length > WIDE_HEADER_TILES_LIMIT
            ? "md:w-1/3 md:shrink-0"
            : "md:w-1/2 md:shrink-0"),
        isSideHeader && position === "right" && "md:order-last"
      )}
    />
  ) : null

  const layoutClass = cn(
    "flex flex-col gap-5",
    isSideHeader && "md:flex-row md:items-stretch"
  )

  // With the header inside the container, the container holds the whole
  // header/tiles row; otherwise the row lives outside and the container wraps
  // the grid alone, becoming the header's row sibling.
  const content =
    headerLayout?.isInsideContainer && hasContainer ? (
      wrapInContainer(
        <div className={layoutClass}>
          {headerNode}

          <div className={rowChildClass}>{grid}</div>
        </div>
      )
    ) : (
      <div className={layoutClass}>
        {headerNode}

        {hasContainer ? (
          wrapInContainer(grid, rowChildClass)
        ) : (
          <div className={rowChildClass}>{grid}</div>
        )}
      </div>
    )

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <OnColorContext.Provider value="backgroundLight">
          {content}
        </OnColorContext.Provider>
      </BaseContainer>
    </BaseSection>
  )
}

export default ProductsTilesSection
